/**
 * Mock Data for Guard Location Stories
 *
 * Sample data for Storybook development and testing.
 */

import { GuardLocationRaw } from './types';
import { transformGuardData } from './utils';

/** Sample raw guard location data */
export const mockGuardsRaw: GuardLocationRaw[] = [
  {
    guardId: 1,
    name: 'John Smith',
    latitude: 43.6532,
    longitude: -79.3832,
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    minutesAgo: 5,
    accuracy: 10,
    batteryPercent: 85,
    deviceInfo: 'iPhone 14',
    currentPatrol: {
      patrolId: 101,
      customerId: 1,
      customerName: 'Acme Corp',
      siteId: 10,
      siteName: 'Downtown Office',
      startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
  },
  {
    guardId: 2,
    name: 'Sarah Johnson',
    latitude: 43.6612,
    longitude: -79.3952,
    timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    minutesAgo: 25,
    accuracy: 15,
    batteryPercent: 42,
    deviceInfo: 'Samsung Galaxy S23',
    currentPatrol: {
      patrolId: 102,
      customerId: 2,
      customerName: 'TechStart Inc',
      siteId: 20,
      siteName: 'Innovation Hub',
      startTime: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    },
  },
  {
    guardId: 3,
    name: 'Mike Chen',
    latitude: 43.6452,
    longitude: -79.3712,
    timestamp: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
    minutesAgo: 3,
    accuracy: 8,
    batteryPercent: 92,
    deviceInfo: 'iPhone 15 Pro',
    currentPatrol: null,
  },
  {
    guardId: 4,
    name: 'Emily Davis',
    latitude: 43.6702,
    longitude: -79.4012,
    timestamp: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
    minutesAgo: 90,
    accuracy: 20,
    batteryPercent: 15,
    deviceInfo: 'Pixel 7',
    currentPatrol: {
      patrolId: 104,
      customerId: 3,
      customerName: 'SecureBank',
      siteId: 30,
      siteName: 'Main Branch',
      startTime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
  },
  {
    guardId: 5,
    name: 'Robert Wilson',
    latitude: 43.6582,
    longitude: -79.3882,
    timestamp: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    minutesAgo: 8,
    accuracy: 12,
    batteryPercent: 67,
    deviceInfo: 'iPhone 13',
    currentPatrol: {
      patrolId: 105,
      customerId: 1,
      customerName: 'Acme Corp',
      siteId: 11,
      siteName: 'Warehouse District',
      startTime: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    },
  },
  {
    guardId: 6,
    name: 'Lisa Martinez',
    latitude: 43.6422,
    longitude: -79.3762,
    timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    minutesAgo: 45,
    accuracy: 25,
    batteryPercent: 33,
    deviceInfo: 'Samsung Galaxy A54',
    currentPatrol: {
      patrolId: 106,
      customerId: 4,
      customerName: 'City Mall',
      siteId: 40,
      siteName: 'Shopping Center',
      startTime: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
  },
  {
    guardId: 7,
    name: 'David Brown',
    latitude: 43.6662,
    longitude: -79.3802,
    timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    minutesAgo: 2,
    accuracy: 5,
    batteryPercent: 78,
    deviceInfo: 'iPhone 14 Pro Max',
    currentPatrol: null,
  },
  {
    guardId: 8,
    name: 'Jennifer Lee',
    latitude: 43.6502,
    longitude: -79.3922,
    timestamp: new Date(Date.now() - 120 * 60 * 1000).toISOString(),
    minutesAgo: 120,
    accuracy: null,
    batteryPercent: null,
    deviceInfo: 'Unknown Device',
    currentPatrol: null,
  },
  {
    guardId: 9,
    name: 'Alex Turner',
    latitude: 43.6550,
    longitude: -79.3750,
    timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    minutesAgo: 10,
    accuracy: 12,
    batteryPercent: 8, // Very low battery!
    deviceInfo: 'Samsung Galaxy S22',
    currentPatrol: {
      patrolId: 109,
      customerId: 5,
      customerName: 'Metro Towers',
      siteId: 50,
      siteName: 'Tower A Lobby',
      startTime: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    },
  },
];

/** Transformed mock guards with derived properties */
export const mockGuards = transformGuardData(mockGuardsRaw);

/** Single active guard for isolated testing */
export const mockActiveGuard = mockGuards.find(g => g.status === 'active')!;

/** Single stale guard for isolated testing */
export const mockStaleGuard = mockGuards.find(g => g.status === 'stale')!;

/** Single offline guard for isolated testing */
export const mockOfflineGuard = mockGuards.find(g => g.status === 'offline')!;

/** Single not-on-patrol guard for isolated testing */
export const mockNotOnPatrolGuard = mockGuards.find(g => g.status === 'not_on_patrol')!;

/** Default map center (Toronto) */
export const DEFAULT_MAP_CENTER = { lat: 43.6532, lng: -79.3832 };

/** Default map zoom level */
export const DEFAULT_MAP_ZOOM = 12;

// ============================================
// Dispatch Stats Mock Data
// ============================================

import type { DispatchStats } from './DispatchStatsRow';

/** Mock dispatch stats - typical day */
export const mockDispatchStats: DispatchStats = {
  guardsOnDuty: 12,
  patrolsInProgress: 5,
  patrolsCompletedToday: 18,
  patrolsTotalToday: 24,
  activeAlerts: 3,
};

/** Mock dispatch stats - no alerts */
export const mockDispatchStatsNoAlerts: DispatchStats = {
  guardsOnDuty: 8,
  patrolsInProgress: 3,
  patrolsCompletedToday: 15,
  patrolsTotalToday: 20,
  activeAlerts: 0,
};

/** Mock dispatch stats - high activity */
export const mockDispatchStatsHighActivity: DispatchStats = {
  guardsOnDuty: 45,
  patrolsInProgress: 18,
  patrolsCompletedToday: 67,
  patrolsTotalToday: 85,
  activeAlerts: 7,
};

/** Mock dispatch stats - low activity */
export const mockDispatchStatsLowActivity: DispatchStats = {
  guardsOnDuty: 2,
  patrolsInProgress: 1,
  patrolsCompletedToday: 3,
  patrolsTotalToday: 5,
  activeAlerts: 0,
};

/** Mock dispatch stats - all patrols complete */
export const mockDispatchStatsAllComplete: DispatchStats = {
  guardsOnDuty: 6,
  patrolsInProgress: 0,
  patrolsCompletedToday: 20,
  patrolsTotalToday: 20,
  activeAlerts: 0,
};

// ============================================
// Enhanced Guard Data (with patrol progress)
// ============================================

import type { PatrolStatus } from './types';

/** Mock guards with enhanced patrol assignment data */
export const mockGuardsWithProgressRaw: GuardLocationRaw[] = [
  {
    guardId: 101,
    name: 'John Doe',
    latitude: 43.6532,
    longitude: -79.3832,
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    minutesAgo: 5,
    accuracy: 10,
    batteryPercent: 85,
    deviceInfo: 'iPhone 14',
    currentPatrol: {
      patrolId: 201,
      customerId: 1,
      customerName: 'Acme Corp',
      siteId: 10,
      siteName: 'Downtown Office',
      startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      patrolStatus: 'in_progress' as PatrolStatus,
      startedAt: new Date(Date.now() - 2 * 60 * 60 * 1000 - 15 * 60 * 1000).toISOString(),
      checkpointsCompleted: 6,
      checkpointsTotal: 10,
    },
  },
  {
    guardId: 102,
    name: 'Jane Smith',
    latitude: 43.6612,
    longitude: -79.3952,
    timestamp: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
    minutesAgo: 3,
    accuracy: 8,
    batteryPercent: 72,
    deviceInfo: 'Samsung Galaxy S23',
    currentPatrol: {
      patrolId: 202,
      customerId: 2,
      customerName: 'TechStart Inc',
      siteId: 20,
      siteName: 'Innovation Hub',
      startTime: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      patrolStatus: 'in_progress' as PatrolStatus,
      startedAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      checkpointsCompleted: 3,
      checkpointsTotal: 8,
    },
  },
  {
    guardId: 103,
    name: 'Mike Wilson',
    latitude: 43.6452,
    longitude: -79.3712,
    timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    minutesAgo: 10,
    accuracy: 15,
    batteryPercent: 45,
    deviceInfo: 'iPhone 13',
    currentPatrol: {
      patrolId: 203,
      customerId: 3,
      customerName: 'SecureBank',
      siteId: 30,
      siteName: 'Main Branch',
      startTime: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      patrolStatus: 'completed' as PatrolStatus,
      startedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      checkpointsCompleted: 12,
      checkpointsTotal: 12,
    },
  },
  {
    guardId: 104,
    name: 'Emily Chen',
    latitude: 43.6702,
    longitude: -79.4012,
    timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    minutesAgo: 2,
    accuracy: 5,
    batteryPercent: 15, // Low battery!
    deviceInfo: 'Pixel 8',
    currentPatrol: {
      patrolId: 204,
      customerId: 4,
      customerName: 'City Mall',
      siteId: 40,
      siteName: 'Shopping Center',
      startTime: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      patrolStatus: 'in_progress' as PatrolStatus,
      startedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      checkpointsCompleted: 2,
      checkpointsTotal: 15,
    },
  },
  {
    guardId: 105,
    name: 'Robert Brown',
    latitude: 43.6582,
    longitude: -79.3882,
    timestamp: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    minutesAgo: 8,
    accuracy: 12,
    batteryPercent: 60,
    deviceInfo: 'iPhone 14 Pro',
    currentPatrol: {
      patrolId: 205,
      customerId: 1,
      customerName: 'Acme Corp',
      siteId: 11,
      siteName: 'Warehouse District',
      startTime: new Date().toISOString(),
      patrolStatus: 'pending' as PatrolStatus,
      startedAt: null,
      checkpointsCompleted: 0,
      checkpointsTotal: 6,
    },
  },
  {
    guardId: 106,
    name: 'Lisa Garcia',
    latitude: 43.6422,
    longitude: -79.3762,
    timestamp: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    minutesAgo: 4,
    accuracy: 10,
    batteryPercent: 88,
    deviceInfo: 'Samsung Galaxy S24',
    currentPatrol: null, // Not on patrol
  },
  {
    guardId: 107,
    name: 'David Kim',
    latitude: 43.6550,
    longitude: -79.3750,
    timestamp: new Date(Date.now() - 6 * 60 * 1000).toISOString(),
    minutesAgo: 6,
    accuracy: 8,
    batteryPercent: 92,
    deviceInfo: 'iPhone 15',
    currentPatrol: {
      patrolId: 207,
      customerId: 5,
      customerName: 'Metro Towers',
      siteId: 50,
      siteName: 'Tower A Lobby',
      startTime: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
      patrolStatus: 'in_progress' as PatrolStatus,
      startedAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
      checkpointsCompleted: 0, // No checkpoints configured
      checkpointsTotal: 0,
    },
  },
];

/** Transformed mock guards with progress */
export const mockGuardsWithProgress = transformGuardData(mockGuardsWithProgressRaw);

/** Single guard with in-progress patrol */
export const mockGuardInProgress = mockGuardsWithProgress.find(
  g => g.currentPatrol?.patrolStatus === 'in_progress' &&
       (g.currentPatrol?.checkpointsTotal || 0) > 0
)!;

/** Single guard with completed patrol */
export const mockGuardCompleted = mockGuardsWithProgress.find(
  g => g.currentPatrol?.patrolStatus === 'completed'
)!;

/** Single guard with pending patrol */
export const mockGuardPending = mockGuardsWithProgress.find(
  g => g.currentPatrol?.patrolStatus === 'pending'
)!;

/** Single guard with low battery */
export const mockGuardLowBattery = mockGuardsWithProgress.find(
  g => (g.batteryPercent || 100) < 20
)!;

/** Single guard not on patrol */
export const mockGuardNoPatrol = mockGuardsWithProgress.find(
  g => !g.currentPatrol
)!;

/** Guard with no checkpoints configured */
export const mockGuardNoCheckpoints = mockGuardsWithProgress.find(
  g => g.currentPatrol?.checkpointsTotal === 0
)!;

// ============================================
// Activity Feed Mock Data
// ============================================

import type { ActivityEvent, ActivityEventType } from './ActivityFeed';

/** Generate a unique event ID */
const eventId = (prefix: string, index: number) => `${prefix}-${index}-${Date.now()}`;

/** Mock activity events */
export const mockActivityEvents: ActivityEvent[] = [
  // Alerts (will be pinned to top)
  {
    eventId: eventId('alert', 1),
    type: 'alert' as ActivityEventType,
    guardId: 104,
    guardName: 'Emily Chen',
    timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    details: {
      alertType: 'no_activity',
      alertMessage: 'No activity for 45 minutes',
      siteName: 'Shopping Center',
    },
  },
  {
    eventId: eventId('alert', 2),
    type: 'alert' as ActivityEventType,
    guardId: 108,
    guardName: 'Chris Taylor',
    timestamp: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    details: {
      alertType: 'left_site',
      alertMessage: 'Left site during patrol',
      siteName: 'Airport Terminal',
    },
  },
  // Regular events
  {
    eventId: eventId('completed', 1),
    type: 'patrol_completed' as ActivityEventType,
    guardId: 103,
    guardName: 'Mike Wilson',
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    details: {
      patrolId: 203,
      siteName: 'Main Branch',
    },
  },
  {
    eventId: eventId('checkpoint', 1),
    type: 'checkpoint_completed' as ActivityEventType,
    guardId: 101,
    guardName: 'John Doe',
    timestamp: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    details: {
      patrolId: 201,
      siteName: 'Downtown Office',
      checkpointName: 'Lobby',
      progress: '6/10',
    },
  },
  {
    eventId: eventId('started', 1),
    type: 'patrol_started' as ActivityEventType,
    guardId: 102,
    guardName: 'Jane Smith',
    timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    details: {
      patrolId: 202,
      siteName: 'Innovation Hub',
    },
  },
  {
    eventId: eventId('checkpoint', 2),
    type: 'checkpoint_completed' as ActivityEventType,
    guardId: 102,
    guardName: 'Jane Smith',
    timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    details: {
      patrolId: 202,
      siteName: 'Innovation Hub',
      checkpointName: 'Server Room',
      progress: '2/8',
    },
  },
  {
    eventId: eventId('arrived', 1),
    type: 'guard_arrived' as ActivityEventType,
    guardId: 107,
    guardName: 'David Kim',
    timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    details: {
      siteName: 'Tower A Lobby',
    },
  },
  {
    eventId: eventId('checkpoint', 3),
    type: 'checkpoint_completed' as ActivityEventType,
    guardId: 101,
    guardName: 'John Doe',
    timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    details: {
      patrolId: 201,
      siteName: 'Downtown Office',
      checkpointName: 'Parking Garage',
      progress: '5/10',
    },
  },
  {
    eventId: eventId('started', 2),
    type: 'patrol_started' as ActivityEventType,
    guardId: 101,
    guardName: 'John Doe',
    timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    details: {
      patrolId: 201,
      siteName: 'Downtown Office',
    },
  },
  {
    eventId: eventId('departed', 1),
    type: 'guard_departed' as ActivityEventType,
    guardId: 106,
    guardName: 'Lisa Garcia',
    timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    details: {
      siteName: 'City Hall',
    },
  },
  {
    eventId: eventId('completed', 2),
    type: 'patrol_completed' as ActivityEventType,
    guardId: 106,
    guardName: 'Lisa Garcia',
    timestamp: new Date(Date.now() - 65 * 60 * 1000).toISOString(),
    details: {
      patrolId: 206,
      siteName: 'City Hall',
    },
  },
];

/** Mock events with no alerts */
export const mockActivityEventsNoAlerts: ActivityEvent[] = mockActivityEvents.filter(
  e => e.type !== 'alert'
);

/** Mock events - alerts only */
export const mockActivityEventsAlertsOnly: ActivityEvent[] = mockActivityEvents.filter(
  e => e.type === 'alert'
);

/** Mock events - high volume */
export const mockActivityEventsHighVolume: ActivityEvent[] = [
  ...mockActivityEvents,
  ...mockActivityEvents.map((e, i) => ({
    ...e,
    eventId: `high-vol-${i}`,
    timestamp: new Date(Date.now() - (70 + i * 5) * 60 * 1000).toISOString(),
    guardName: `Guard ${i + 10}`,
  })),
];

// ============================================
// Alerts Bar Mock Data
// ============================================

import type { DispatchAlert, AlertType, AlertSeverity } from './AlertsBar';

/** Mock dispatch alerts */
export const mockDispatchAlerts: DispatchAlert[] = [
  {
    alertId: 'alert-001',
    type: 'no_activity' as AlertType,
    severity: 'critical' as AlertSeverity,
    guardId: 104,
    guardName: 'Emily Chen',
    message: 'No activity detected for 45 minutes',
    details: {
      minutesSinceActivity: 45,
      siteName: 'Shopping Center',
      patrolId: 204,
    },
    createdAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
  },
  {
    alertId: 'alert-002',
    type: 'left_site' as AlertType,
    severity: 'critical' as AlertSeverity,
    guardId: 108,
    guardName: 'Chris Taylor',
    message: 'Guard left site during active patrol',
    details: {
      siteName: 'Airport Terminal',
      patrolId: 208,
    },
    createdAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
  },
  {
    alertId: 'alert-003',
    type: 'incomplete_patrol' as AlertType,
    severity: 'warning' as AlertSeverity,
    guardId: 109,
    guardName: 'Mike Jones',
    message: 'Patrol running longer than expected',
    details: {
      siteName: 'Mall Security',
      patrolId: 209,
      startedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    alertId: 'alert-004',
    type: 'low_battery' as AlertType,
    severity: 'warning' as AlertSeverity,
    guardId: 104,
    guardName: 'Emily Chen',
    message: 'Battery level critically low',
    details: {
      batteryPercent: 8,
      siteName: 'Shopping Center',
    },
    createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
  },
  {
    alertId: 'alert-005',
    type: 'stale_patrol' as AlertType,
    severity: 'warning' as AlertSeverity,
    guardId: 110,
    guardName: 'Sarah Williams',
    message: 'No location update in 2+ hours',
    details: {
      siteName: 'Office Complex',
      patrolId: 210,
      startedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
    createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
  },
];

/** Mock alerts - warnings only (no critical) */
export const mockAlertsWarningsOnly: DispatchAlert[] = mockDispatchAlerts.filter(
  a => a.severity === 'warning'
);

/** Mock alerts - critical only */
export const mockAlertsCriticalOnly: DispatchAlert[] = mockDispatchAlerts.filter(
  a => a.severity === 'critical'
);

/** Mock alerts - single alert */
export const mockAlertsSingle: DispatchAlert[] = [mockDispatchAlerts[0]];

/** Mock alerts - with one dismissed */
export const mockAlertsWithDismissed: DispatchAlert[] = [
  ...mockDispatchAlerts.slice(0, 3),
  {
    ...mockDispatchAlerts[3],
    dismissedAt: new Date().toISOString(),
  },
  mockDispatchAlerts[4],
];

// ============================================
// Grouped List Mock Data (30+ guards)
// ============================================

/** Generate mock guards for grouped list testing */
function generateManyGuards(count: number): GuardLocationRaw[] {
  const customers = [
    { id: 1, name: 'Acme Corp', sites: ['Downtown Office', 'Warehouse', 'HQ'] },
    { id: 2, name: 'TechStart Inc', sites: ['Innovation Hub', 'Research Lab'] },
    { id: 3, name: 'SecureBank', sites: ['Main Branch', 'North Branch', 'South Branch'] },
    { id: 4, name: 'City Mall', sites: ['Shopping Center', 'Parking Garage'] },
    { id: 5, name: 'Metro Towers', sites: ['Tower A', 'Tower B', 'Tower C'] },
    { id: 6, name: 'Airport Authority', sites: ['Terminal 1', 'Terminal 2', 'Cargo Area'] },
    { id: 7, name: 'University Campus', sites: ['Library', 'Science Building', 'Dormitory'] },
  ];

  const firstNames = ['John', 'Jane', 'Mike', 'Emily', 'Robert', 'Lisa', 'David', 'Sarah', 'Chris', 'Amanda'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Wilson', 'Taylor'];

  const guards: GuardLocationRaw[] = [];

  for (let i = 0; i < count; i++) {
    const customer = customers[i % customers.length];
    const site = customer.sites[i % customer.sites.length];
    const firstName = firstNames[i % firstNames.length];
    const lastName = lastNames[Math.floor(i / firstNames.length) % lastNames.length];
    const minutesAgo = Math.floor(Math.random() * 120);
    const batteryPercent = Math.floor(Math.random() * 100);
    const hasPatrol = Math.random() > 0.15; // 85% on patrol

    guards.push({
      guardId: 1000 + i,
      name: `${firstName} ${lastName}`,
      latitude: 43.6532 + (Math.random() - 0.5) * 0.1,
      longitude: -79.3832 + (Math.random() - 0.5) * 0.1,
      timestamp: new Date(Date.now() - minutesAgo * 60 * 1000).toISOString(),
      minutesAgo,
      accuracy: Math.floor(Math.random() * 20) + 5,
      batteryPercent,
      deviceInfo: ['iPhone 14', 'Samsung Galaxy S23', 'Pixel 8'][i % 3],
      currentPatrol: hasPatrol ? {
        patrolId: 5000 + i,
        customerId: customer.id,
        customerName: customer.name,
        siteId: customer.id * 10 + (i % customer.sites.length),
        siteName: site,
        startTime: new Date(Date.now() - (minutesAgo + 30) * 60 * 1000).toISOString(),
        patrolStatus: minutesAgo < 60 ? 'in_progress' as PatrolStatus : 'completed' as PatrolStatus,
        startedAt: new Date(Date.now() - (minutesAgo + 30) * 60 * 1000).toISOString(),
        checkpointsCompleted: Math.floor(Math.random() * 8),
        checkpointsTotal: 8,
      } : null,
    });
  }

  return guards;
}

/** Mock guards - 35 guards (above grouping threshold) */
export const mockGuardsManyRaw = generateManyGuards(35);
export const mockGuardsMany = transformGuardData(mockGuardsManyRaw);

/** Mock guards - 50 guards (well above threshold) */
export const mockGuards50Raw = generateManyGuards(50);
export const mockGuards50 = transformGuardData(mockGuards50Raw);

/** Mock guards - 20 guards (below grouping threshold) */
export const mockGuards20Raw = generateManyGuards(20);
export const mockGuards20 = transformGuardData(mockGuards20Raw);
